import UserCardPopper from "@/components/communications-chat-components/UserCardPopper";
import { Colors } from "@/enums/Colors";
import { LazoUser } from "@/models/LazoUser";
import LazoUserService from "@/services/LazoUserService";
import { BoxProps } from "@mui/material";
import { Box, styled } from "@mui/system";
import { useCallback, useMemo, useRef, useState } from "react";

type ChatMessageMentionProps = {
  userExternalId: string;
  content: string;
  boxSx?: BoxProps["sx"];
};

function ChatMessageMention(props: ChatMessageMentionProps) {
  const mentionRef = useRef<HTMLDivElement>(null);
  const [showUserCard, setShowUserCard] = useState<boolean>(false);
  const [user, setUser] = useState<LazoUser | null>(null);
  const [closeCardTimeoutHandle, setCloseCardTimeoutHandle] = useState<NodeJS.Timeout | null>(null);
  const [showCardTimeoutHandle, setShowCardTimeoutHandle] = useState<NodeJS.Timeout | null>(null);

  const fetchUser = useCallback(() => {
    if (user && user.user.externalId === props.userExternalId) return;
    LazoUserService.getByCoreUserExternalId(props.userExternalId).then((r) => {
      r.onSuccess((c) => {
        setUser(c);
      });
    });
  }, [props.userExternalId]);

  const anchorEl = useMemo(() => {
    if (mentionRef.current) {
      return mentionRef.current;
    }
  }, [mentionRef.current]);

  function handleMentionMouseEnter() {
    if (showCardTimeoutHandle) return;
    const tHandle = setTimeout(() => {
      setShowUserCard(true);
      setShowCardTimeoutHandle(null);
    }, 300);
    setShowCardTimeoutHandle(tHandle);
  }

  function handleMentionMouseLeave() {
    if (showCardTimeoutHandle !== null) {
      clearTimeout(showCardTimeoutHandle);
      setShowCardTimeoutHandle(null);
    }
    if (closeCardTimeoutHandle !== null) return;
    const tHandle = setTimeout(() => {
      setShowUserCard(false);
      setCloseCardTimeoutHandle(null);
    }, 300);
    setCloseCardTimeoutHandle(tHandle);
  }

  return (
    <>
      <MentionContainer
        ref={mentionRef}
        onMouseEnter={() => {
          fetchUser();
          handleMentionMouseEnter();
        }}
        onMouseLeave={() => handleMentionMouseLeave()}
        sx={{ ...props.boxSx }}
      >
        @{props.content}
      </MentionContainer>
      <UserCardPopper
        anchorEl={anchorEl}
        lazoUser={user}
        open={showUserCard}
        setOpen={setShowUserCard}
        closeCardTimeoutHandle={closeCardTimeoutHandle}
        setCloseCardTimeoutHandle={setCloseCardTimeoutHandle}
      />
    </>
  );
}

const MentionContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "0.25rem",
  color: Colors.BLACK_151517,
  fontSize: "0.75rem",
  fontWeight: 500,
  borderRadius: "0.125rem",
  backgroundColor: Colors.ACCENT_BLUE_DESATURATED_CED5FD,
  ":hover": {
    color: Colors.BLUE_4763F4,
  },
}));

export default ChatMessageMention;
